// src/utils/scoreUtils.js
// スコア表示の共通ユーティリティ（色 + アイコン + グラデーション）

import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * スコアに応じた色を取得
 * @param {number|null} score - スコア（0-100）
 * @returns {string} CSS色コード
 */
export function getScoreColor(score) {
  if (score === null || score === undefined) return '#B0B8C4';
  if (score >= 80) return '#4CAF50';
  if (score >= 60) return '#FF9800';
  if (score >= 40) return '#2196F3';
  return '#f44336';
}

/**
 * スコアに応じたグラデーションを取得
 * @param {number|null} score - スコア（0-100）
 * @returns {string} CSSグラデーション
 */
export function getScoreGradient(score) {
  if (score === null || score === undefined) return 'linear-gradient(135deg, #B0B8C4 0%, #DDE2E8 100%)';
  if (score >= 80) return 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)';
  if (score >= 60) return 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)';
  if (score >= 40) return 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)';
  return 'linear-gradient(135deg, #f44336 0%, #EF5350 100%)';
}

/**
 * スコアに応じたボーダー色を取得
 * @param {number|null} score - スコア（0-100）
 * @returns {string} CSS色コード
 */
export function getScoreBorderColor(score) {
  if (score === null || score === undefined) return '#DDE2E8';
  if (score >= 80) return '#4CAF50';
  if (score >= 60) return '#FF9800';
  return '#f44336';
}

/**
 * スコアに応じた背景色（薄い）を取得
 * @param {number|null} score - スコア（0-100）
 * @returns {string} CSS色コード
 */
export function getScoreBackgroundColor(score) {
  if (score === null || score === undefined) return 'transparent';
  if (score >= 80) return 'rgba(76, 175, 80, 0.06)';
  if (score >= 60) return 'rgba(255, 152, 0, 0.06)';
  return 'rgba(244, 67, 54, 0.06)';
}

/**
 * スコアに応じたステータスラベルを取得
 * @param {number|null} score - スコア（0-100）
 * @returns {string} ステータスラベル
 */
export function getScoreStatusLabel(score) {
  if (score === null || score === undefined) return '';
  if (score >= 80) return '良好';
  if (score >= 60) return '改善余地あり';
  return '要改善';
}

/**
 * スコアに応じたアイコンを取得（アクセシビリティ強化）
 * @param {number|null} score - スコア（0-100）
 * @param {number} fontSize - アイコンサイズ（デフォルト: 16）
 * @returns {React.ReactElement|null} MUIアイコン
 */
export function getScoreIcon(score, fontSize = 16) {
  if (score === null || score === undefined) return null;
  if (score >= 80) return <CheckCircleIcon sx={{ fontSize, color: '#4CAF50' }} />;
  if (score >= 60) return <WarningIcon sx={{ fontSize, color: '#FF9800' }} />;
  return <ErrorIcon sx={{ fontSize, color: '#f44336' }} />;
}
